'use client';

import { useSuspenseQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type { GetManyRequestType } from '@shared-types/common';
import type { DocPabean, DocPabeanResponse } from '../models/doc-pabean-model';
import { getDemoDocPabeans, isDemoUser } from '../demo-data';

type DocPabeanQueryParams = GetManyRequestType & {
  orderBy?: string;
  ascending?: string;
  statusFilter?: string;
  /** Include soft-deleted documents in the list. Defaults false. */
  includeDeleted?: boolean;
  /**
   * Branch (cabang) to list. Empty is NOT "no filter" — it means "my own
   * branch", which the API resolves from the session. Send BRANCH_ALL to see
   * every branch.
   */
  branchFilter?: string;
};

type DocPabeanQueryResult = {
  data: DocPabean[];
  totalCount: number;
  page: number;
};

async function fetchDocPabeans({
  page = 1,
  itemPerPage = 10,
  searchQuery = '',
  orderBy = 'id',
  ascending = 'false',
  statusFilter = '',
  includeDeleted = false,
  branchFilter = '',
  signal,
}: DocPabeanQueryParams & { signal: AbortSignal }): Promise<DocPabeanQueryResult> {
  try {
    const offset = (Math.max(1, page) - 1) * itemPerPage;

    if (isDemoUser()) {
      const query = searchQuery.trim().toLowerCase();
      const allData = getDemoDocPabeans();

      const filtered = query
        ? allData.filter((item) => {
            const haystacks = [
              item.nomorAju,
              item.namaPerusahaan,
              item.namaEntitas,
              item.drafterName,
              item.shipperConsigneeCountry,
            ].map((value) => value?.toString().toLowerCase() ?? '');
            return haystacks.some((value) => value.includes(query));
          })
        : allData;

      const demoSortFields: Record<string, keyof DocPabean> = {
        ajuNo: 'nomorAju',
        namaPerusahaan: 'namaPerusahaan',
        tanggalDaftar: 'tanggalDaftar',
        totalItem: 'totalItem',
      };
      const sortField = demoSortFields[orderBy];
      const sorted = sortField
        ? [...filtered].sort((a, b) => {
            const left = a[sortField];
            const right = b[sortField];
            const result =
              typeof left === 'number' || typeof right === 'number'
                ? Number(left ?? 0) - Number(right ?? 0)
                : (left ?? '').toString().localeCompare((right ?? '').toString());
            return ascending === 'true' ? result : -result;
          })
        : filtered;

      const sliced = sorted.slice(offset, offset + itemPerPage);

      return {
        data: sliced,
        totalCount: sorted.length,
        page: Math.max(1, page),
      };
    }

    const params: Record<string, string> = {
      offset: offset.toString(),
      limit: itemPerPage.toString(),
      orderBy,
      ascending,
    };

    if (searchQuery) {
      params.query = searchQuery;
    }

    if (statusFilter) {
      params.respon = statusFilter;
    }

    // Opt-in only: without it the API returns live rows exactly as before.
    if (includeDeleted) {
      params.includeDeleted = 'true';
    }

    // Omitted on purpose when empty: the API reads an absent `branch` as the
    // caller's own branch. Sending branch='' would be a different request.
    if (branchFilter) {
      params.branch = branchFilter;
    }

    const { data: resultResponse } = await axiosInstance.get<{
      data: DocPabeanResponse;
    }>(apiUrl.docPabean.list, {
      signal,
      params,
    });

    return {
      data: resultResponse.data.data,
      totalCount: resultResponse.data.totalCount,
      page: resultResponse.data.page,
    };
  } catch (error: any) {
    if (error?.code === 'ERR_CANCELED' || error?.message === 'canceled') {
      throw error;
    }

    const message = error?.response?.data?.message || error?.message || 'Request failed!';
    throw new Error(message);
  }
}

export default function useGetDocPabeans(params: DocPabeanQueryParams) {
  const {
    page = 1,
    itemPerPage = 10,
    searchQuery = '',
    orderBy = 'id',
    ascending = 'false',
    statusFilter = '',
    includeDeleted = false,
    branchFilter = '',
  } = params;

  return useSuspenseQuery({
    queryKey: [
      'doc-pabeans',
      {
        page: Math.max(1, page),
        itemPerPage,
        searchQuery,
        orderBy,
        ascending,
        statusFilter,
        includeDeleted,
        // Part of the key, not just the request: without it a branch switch
        // would be served the previous branch's rows from cache.
        branchFilter,
      },
    ],
    queryFn: ({ signal }) =>
      fetchDocPabeans({
        page,
        itemPerPage,
        searchQuery,
        orderBy,
        ascending,
        statusFilter,
        includeDeleted,
        branchFilter,
        signal,
      }),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    // Refetch when the page is opened — same reason as the draft list: with
    // this false and a 5-minute staleTime, a document that had just changed
    // state did not show up until the cache expired or the browser was
    // hard-refreshed.
    refetchOnMount: 'always',
    refetchOnReconnect: false,
  });
}
